import { Component, effect, signal } from '@angular/core';
import { Router, RouterLink, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from './core/services/auth.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { ThemeService } from './core/services/theme.service';
import { SubdomainService, catanUrl } from './core/services/subdomain.service';
import { CatanShellComponent } from './features/catan/shared/catan-shell.component';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Početna',
  '/calendar': 'Kalendar',
  '/projects': 'Projekti',
  '/receipts': 'Računi',
  '/media': 'Mediji',
  '/shopping': 'Shopping',
  '/budget': 'Budžet',
  '/chores': 'Kućanske obaveze',
  '/meal-reminders': 'Meal reminders',
  '/notes': 'Bilješke',
  '/cameras': 'Kamere',
  '/special-dates': 'Posebni datumi',
  '/family': 'Obitelj',
  '/family/create': 'Kreiraj obitelj',
  '/family/join': 'Pridruži se obitelji',
  '/profile': 'Moj profil',
  '/admin': 'Admin',
  '/admin/dashboard': 'Admin — Dashboard',
  '/admin/users': 'Admin — Korisnici',
  '/admin/families': 'Admin — Obitelji',
  '/admin/settings': 'Admin — Postavke',
};

/** Naslovi taba na catan.my-famil.io — obiteljski sidebar se ondje ne renderira. */
const CATAN_ROUTE_TITLES: Record<string, string> = {
  '/catan': 'Catan scoreboard',
  '/catan/games': 'Catan — Igre',
  '/catan/players': 'Catan — Igrači',
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, ButtonModule, DrawerModule, ToastModule, TooltipModule, CatanShellComponent],
  template: `
    <p-toast
      position="bottom-right"
      [breakpoints]="{ '768px': { width: '100%', right: '0', left: '0', bottom: '70px' } }"
    />

    @if (isFamilyApp() && auth.isLoggedIn()) {
      <!-- Left sidebar: desktop only -->
      <aside class="app-sidebar">
        <a [routerLink]="isAdmin() ? '/admin/dashboard' : '/dashboard'" class="sidebar-header">
          @if (subdomainService.familyBranding()?.brandLogoUrl) {
            <img [src]="subdomainService.familyBranding()!.brandLogoUrl" alt="Logo" class="sidebar-logo" />
          } @else {
            <img src="assets/icons/logo.svg" alt="My Familio" class="sidebar-logo" />
          }
          <span class="sidebar-brand">{{ subdomainService.familyBranding()?.familyName ?? 'My Familio' }}</span>
        </a>

        <nav class="sidebar-nav">
          @if (isAdmin()) {
            <a
              routerLink="/admin/dashboard"
              class="sidebar-nav__item"
              [class.active]="currentUrl() === '/admin/dashboard' || currentUrl() === '/admin'"
            >
              <i class="pi pi-chart-bar"></i>
              <span>Dashboard</span>
            </a>
            <a
              routerLink="/admin/users"
              class="sidebar-nav__item"
              [class.active]="currentUrl().startsWith('/admin/users')"
            >
              <i class="pi pi-users"></i>
              <span>Korisnici</span>
            </a>
            <a
              routerLink="/admin/families"
              class="sidebar-nav__item"
              [class.active]="currentUrl().startsWith('/admin/families')"
            >
              <i class="pi pi-home"></i>
              <span>Obitelji</span>
            </a>
            <a
              routerLink="/admin/settings"
              class="sidebar-nav__item"
              [class.active]="currentUrl().startsWith('/admin/settings')"
            >
              <i class="pi pi-cog"></i>
              <span>Postavke</span>
            </a>
          } @else {
            <a routerLink="/dashboard" class="sidebar-nav__item" [class.active]="currentUrl() === '/dashboard'">
              <i class="pi pi-home"></i>
              <span>Početna</span>
            </a>
            <a routerLink="/calendar" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/calendar')">
              <i class="pi pi-calendar"></i>
              <span>Kalendar</span>
            </a>
            <a routerLink="/family" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/family')">
              <i class="pi pi-users"></i>
              <span>Obitelj</span>
            </a>

            <div class="sidebar-nav__group-label">Financije</div>
            <a routerLink="/uplatnice" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/uplatnice')">
              <i class="pi pi-credit-card"></i>
              <span>Uplatnice</span>
            </a>
            <a routerLink="/receipts" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/receipts')">
              <i class="pi pi-receipt"></i>
              <span>Računi</span>
            </a>
            <a routerLink="/budget" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/budget')">
              <i class="pi pi-chart-pie"></i>
              <span>Budžet</span>
            </a>
            <a routerLink="/shopping" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/shopping')">
              <i class="pi pi-shopping-cart"></i>
              <span>Shopping</span>
            </a>

            <div class="sidebar-nav__group-label">Planiranje</div>
            <a routerLink="/projects" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/projects')">
              <i class="pi pi-briefcase"></i>
              <span>Projekti</span>
            </a>
            <a routerLink="/chores" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/chores')">
              <i class="pi pi-check-square"></i>
              <span>Obaveze</span>
            </a>
            <a
              routerLink="/meal-reminders"
              class="sidebar-nav__item"
              [class.active]="currentUrl().startsWith('/meal-reminders')"
            >
              <i class="pi pi-heart"></i>
              <span>Prehrana</span>
            </a>
            <a routerLink="/notes" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/notes')">
              <i class="pi pi-file-edit"></i>
              <span>Bilješke</span>
            </a>
            <a
              routerLink="/special-dates"
              class="sidebar-nav__item"
              [class.active]="currentUrl().startsWith('/special-dates')"
            >
              <i class="pi pi-star"></i>
              <span>Datumi</span>
            </a>

            <div class="sidebar-nav__group-label">Dom</div>
            <a routerLink="/media" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/media')">
              <i class="pi pi-images"></i>
              <span>Mediji</span>
            </a>
            <a routerLink="/cameras" class="sidebar-nav__item" [class.active]="currentUrl().startsWith('/cameras')">
              <i class="pi pi-video"></i>
              <span>Kamere</span>
            </a>
          }

          @if (catanHref) {
            <div class="sidebar-nav__group-label">Društvo</div>
            <!-- Catan je vlastita subdomena, pa ide obican href a ne routerLink. -->
            <a [href]="catanHref" class="sidebar-nav__item">
              <span class="sidebar-nav__emoji" aria-hidden="true">🎲</span>
              <span>Catan</span>
              <i class="pi pi-external-link sidebar-nav__external"></i>
            </a>
          }
        </nav>

        <div class="sidebar-footer">
          <a
            routerLink="/profile"
            class="sidebar-user"
            style="text-decoration: none; color: inherit; flex: 1; min-width: 0"
          >
            {{ auth.currentUser()?.fullName }}
          </a>
          <p-button
            [icon]="theme.darkMode() ? 'pi pi-sun' : 'pi pi-moon'"
            [text]="true"
            severity="secondary"
            size="small"
            [pTooltip]="theme.darkMode() ? 'Svijetli način' : 'Tamni način'"
            tooltipPosition="top"
            (onClick)="theme.toggle()"
          />
          <p-button icon="pi pi-sign-out" [text]="true" severity="secondary" size="small" (onClick)="auth.logout()" />
        </div>
      </aside>

      <!-- Mobile top header -->
      <header class="app-header">
        <a [routerLink]="isAdmin() ? '/admin/dashboard' : '/dashboard'" class="app-header__logo">
          @if (subdomainService.familyBranding()?.brandLogoUrl) {
            <img [src]="subdomainService.familyBranding()!.brandLogoUrl" alt="Logo" width="28" height="28" />
          } @else {
            <img src="assets/icons/logo.svg" alt="My Familio" width="28" height="28" />
          }
        </a>
        <span class="app-header__title">{{ pageTitle() }}</span>
        <div style="display: flex; align-items: center; gap: 0.25rem">
          <p-button
            [icon]="theme.darkMode() ? 'pi pi-sun' : 'pi pi-moon'"
            [text]="true"
            severity="secondary"
            size="small"
            (onClick)="theme.toggle()"
          />
          <p-button icon="pi pi-sign-out" [text]="true" severity="secondary" size="small" (onClick)="auth.logout()" />
        </div>
      </header>
    }

    @if (subdomainService.isCatan()) {
      <app-catan-shell />
    }

    <main class="app-main" [class.catan-main]="subdomainService.isCatan()">
      <router-outlet />
    </main>

    @if (isFamilyApp() && auth.isLoggedIn()) {
      <!-- Mobile bottom nav -->
      <nav class="bottom-nav">
        @if (isAdmin()) {
          <a
            routerLink="/admin/dashboard"
            class="bottom-nav__item"
            [class.active]="currentUrl() === '/admin/dashboard' || currentUrl() === '/admin'"
          >
            <i class="pi pi-chart-bar"></i>
            <span>Dashboard</span>
          </a>
          <a
            routerLink="/admin/users"
            class="bottom-nav__item"
            [class.active]="currentUrl().startsWith('/admin/users')"
          >
            <i class="pi pi-users"></i>
            <span>Korisnici</span>
          </a>
          <a
            routerLink="/admin/families"
            class="bottom-nav__item"
            [class.active]="currentUrl().startsWith('/admin/families')"
          >
            <i class="pi pi-home"></i>
            <span>Obitelji</span>
          </a>
          <a
            routerLink="/admin/settings"
            class="bottom-nav__item"
            [class.active]="currentUrl().startsWith('/admin/settings')"
          >
            <i class="pi pi-cog"></i>
            <span>Postavke</span>
          </a>
        } @else {
          <a routerLink="/dashboard" class="bottom-nav__item" [class.active]="currentUrl() === '/dashboard'">
            <i class="pi pi-home"></i>
            <span>Početna</span>
          </a>
          <a routerLink="/calendar" class="bottom-nav__item" [class.active]="currentUrl().startsWith('/calendar')">
            <i class="pi pi-calendar"></i>
            <span>Kalendar</span>
          </a>
          <a routerLink="/family" class="bottom-nav__item" [class.active]="currentUrl().startsWith('/family')">
            <i class="pi pi-users"></i>
            <span>Obitelj</span>
          </a>
          <a routerLink="/projects" class="bottom-nav__item" [class.active]="currentUrl().startsWith('/projects')">
            <i class="pi pi-briefcase"></i>
            <span>Projekti</span>
          </a>
          <a class="bottom-nav__item" [class.active]="moreDrawerVisible" (click)="moreDrawerVisible = true">
            <i class="pi pi-bars"></i>
            <span>Više</span>
          </a>
        }
      </nav>

      <!-- Mobile "Više" drawer -->
      <p-drawer
        [(visible)]="moreDrawerVisible"
        position="bottom"
        [style]="{ height: 'auto', 'max-height': '85vh' }"
        [modal]="true"
      >
        <ng-template #header>
          <div class="flex align-items-center gap-3" style="width: 100%">
            @if (subdomainService.familyBranding()?.brandLogoUrl) {
              <img
                [src]="subdomainService.familyBranding()!.brandLogoUrl"
                alt="Logo"
                width="28"
                height="28"
                style="border-radius: 6px"
              />
            } @else {
              <img src="assets/icons/logo.svg" alt="My Familio" width="28" height="28" style="border-radius: 6px" />
            }
            <span style="font-weight: 700; font-size: 1rem">{{
              subdomainService.familyBranding()?.familyName ?? 'My Familio'
            }}</span>
          </div>
        </ng-template>
        <nav class="mobile-drawer-nav">
          <a
            routerLink="/dashboard"
            class="mobile-drawer-item"
            [class.active]="currentUrl() === '/dashboard'"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-home"></i><span>Početna</span>
          </a>
          <a
            routerLink="/calendar"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/calendar')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-calendar"></i><span>Kalendar</span>
          </a>
          <a
            routerLink="/family"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/family')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-users"></i><span>Obitelj</span>
          </a>

          <div class="mobile-drawer-group-label">Financije</div>
          <a
            routerLink="/uplatnice"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/uplatnice')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-credit-card"></i><span>Uplatnice</span>
          </a>
          <a
            routerLink="/receipts"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/receipts')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-receipt"></i><span>Računi</span>
          </a>
          <a
            routerLink="/budget"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/budget')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-chart-pie"></i><span>Budžet</span>
          </a>
          <a
            routerLink="/shopping"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/shopping')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-shopping-cart"></i><span>Shopping</span>
          </a>

          <div class="mobile-drawer-group-label">Planiranje</div>
          <a
            routerLink="/projects"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/projects')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-briefcase"></i><span>Projekti</span>
          </a>
          <a
            routerLink="/chores"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/chores')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-check-square"></i><span>Obaveze</span>
          </a>
          <a
            routerLink="/meal-reminders"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/meal-reminders')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-heart"></i><span>Prehrana</span>
          </a>
          <a
            routerLink="/notes"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/notes')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-file-edit"></i><span>Bilješke</span>
          </a>
          <a
            routerLink="/special-dates"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/special-dates')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-star"></i><span>Datumi</span>
          </a>

          <div class="mobile-drawer-group-label">Dom</div>
          <a
            routerLink="/media"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/media')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-images"></i><span>Mediji</span>
          </a>
          <a
            routerLink="/cameras"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/cameras')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-video"></i><span>Kamere</span>
          </a>

          @if (catanHref) {
            <div class="mobile-drawer-group-label">Društvo</div>
            <a [href]="catanHref" class="mobile-drawer-item" (click)="moreDrawerVisible = false">
              <span class="sidebar-nav__emoji" aria-hidden="true">🎲</span><span>Catan</span>
            </a>
          }

          <div style="border-top: 1px solid var(--p-content-border-color); margin: 0.5rem 0"></div>

          <a
            routerLink="/profile"
            class="mobile-drawer-item"
            [class.active]="currentUrl().startsWith('/profile')"
            (click)="moreDrawerVisible = false"
          >
            <i class="pi pi-user"></i><span>Moj profil</span>
          </a>

          @if (pwa.canInstall() && !pwa.isStandalone()) {
            <a class="mobile-drawer-item" (click)="pwa.install(); moreDrawerVisible = false">
              <i class="pi pi-download"></i><span>Instaliraj aplikaciju</span>
            </a>
          }

          <a class="mobile-drawer-item" (click)="theme.toggle(); moreDrawerVisible = false">
            <i [class]="theme.darkMode() ? 'pi pi-sun' : 'pi pi-moon'"></i>
            <span>{{ theme.darkMode() ? 'Svijetli način' : 'Tamni način' }}</span>
          </a>

          <a class="mobile-drawer-item" style="color: var(--p-red-500)" (click)="auth.logout()">
            <i class="pi pi-sign-out"></i><span>Odjava</span>
          </a>
        </nav>
      </p-drawer>
    }
  `,
})
export class AppComponent {
  pageTitle = signal('My Familio');
  currentUrl = signal('');
  moreDrawerVisible = false;

  /** Apsolutni URL Catan subdomene; null na hostu koji subdomene ne podrzava. */
  readonly catanHref = catanUrl();

  isAdmin(): boolean {
    return this.auth.currentUser()?.roles?.includes('ROLE_ADMIN') ?? false;
  }

  /** Obiteljski chrome (sidebar, bottom nav, drawer) se ne prikazuje u Catan modu. */
  isFamilyApp(): boolean {
    return !this.subdomainService.isCatan();
  }

  constructor(
    public auth: AuthService,
    public pwa: PwaInstallService,
    public theme: ThemeService,
    public subdomainService: SubdomainService,
    private router: Router,
  ) {
    this.theme.init();
    this.subdomainService.init();

    // Catan akcent je scopean na .catan-mode, uz .dark-mode na istom elementu.
    if (this.subdomainService.isCatan()) {
      document.documentElement.classList.add('catan-mode');
    }

    if (this.auth.isLoggedIn() && !this.auth.currentUser()) {
      this.auth.loadCurrentUser();
    }

    effect(() => {
      const branding = this.subdomainService.familyBranding();
      if (branding?.brandPrimaryColor) {
        this.theme.applyFamilyBranding(branding.brandPrimaryColor);
      }
    });

    effect(() => {
      const user = this.auth.currentUser();
      // Catan je zaseban proizvod — admin koji ga otvori ne smije zavrsiti na admin dashboardu.
      if (this.isFamilyApp() && user?.roles?.includes('ROLE_ADMIN') && !this.currentUrl().startsWith('/admin')) {
        this.router.navigate(['/admin/dashboard']);
      }
    });

    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      const path = e.urlAfterRedirects.split('?')[0];
      this.currentUrl.set(path);
      const title = this.titleFor(path);
      this.pageTitle.set(title);
      document.title = title;
    });
  }

  private titleFor(path: string): string {
    if (this.subdomainService.isCatan()) {
      return CATAN_ROUTE_TITLES[path] ?? (path.startsWith('/catan/') ? 'Catan' : 'Catan scoreboard');
    }

    return ROUTE_TITLES[path] ?? (path.startsWith('/family') ? 'Obitelj' : 'My Familio');
  }
}
